import type { Sector } from '@/types/theme.types';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { fill } from '../copy';
import type { InitialDataCopy } from '../initial-data-copy';

type SectorPickerProps = {
  copy: InitialDataCopy;
  sectors: Sector[];
  selectedId: string;
  isLoading: boolean;
  hasError: boolean;
  onSelect: (sectorId: string) => void;
  onRetry: () => void;
};

/** The sectors as a radio grid, with their number of questions. */
export function SectorPicker({ copy, sectors, selectedId, isLoading, hasError, onSelect, onRetry }: SectorPickerProps) {
  if (isLoading) {
    return (
      <div className="grid gap-2 sm:grid-cols-2" aria-busy="true" aria-label={copy.sectorsLoading}>
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} className="h-16 rounded-lg bg-muted/40" />
        ))}
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-danger/25 bg-danger/[0.06] px-4 py-3 text-sm text-danger">
        <span>{copy.sectorsError}</span>
        <button type="button" onClick={onRetry} className="font-medium underline-offset-4 hover:underline">
          {copy.retry}
        </button>
      </div>
    );
  }

  if (sectors.length === 0) {
    return <p className="rounded-lg border border-border/60 bg-surface-2 px-4 py-3 text-sm text-muted-foreground">{copy.sectorsEmpty}</p>;
  }

  return (
    <div role="radiogroup" aria-label={copy.sectorLabel} className="grid max-h-72 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
      {sectors.map((sector) => {
        const selected = sector.id === selectedId;
        return (
          <button
            key={sector.id}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={`initial-data-sector-${sector.id}`}
            onClick={() => onSelect(sector.id)}
            className={cn(
              'flex min-h-16 flex-col items-start justify-center rounded-lg border px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected ? 'border-primary bg-primary/[0.06] text-foreground' : 'border-border/60 bg-surface text-foreground hover:border-brand/40',
            )}
          >
            <span className="line-clamp-1 text-sm font-medium">{sector.name}</span>
            {typeof sector.questionsCount === 'number' && (
              <span className="mt-0.5 text-xs text-muted-foreground">{fill(copy.sectorQuestions, { count: sector.questionsCount })}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
